import { useState } from 'react';
import { AlertTriangle, X, Eye, Image, Video, File } from 'lucide-react';
import { MediaItem } from '../types/api';

interface DeleteConfirmModalProps {
  items: MediaItem[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteConfirmModal({
  items,
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false
}: DeleteConfirmModalProps) {
  const [confirmed, setConfirmed] = useState(false);

  const isBulkOperation = items.length > 1;
  const needsDoubleConfirmation = items.length > 5;

  const handleConfirm = () => {
    if (needsDoubleConfirmation && !confirmed) {
      setConfirmed(true);
      return;
    }
    onConfirm();
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmed(false);
      onClose();
    }
  };

  const getIcon = (type: MediaItem['type']) => {
    switch (type) {
      case 'image':
        return <Image className="h-4 w-4" />;
      case 'video':
        return <Video className="h-4 w-4" />;
      default:
        return <File className="h-4 w-4" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h2>
              <p className="text-sm text-gray-500">
                {isBulkOperation
                  ? `Delete ${items.length} items permanently?`
                  : 'Delete this item permanently?'
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Warning Message */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 mb-1">
                  This action cannot be undone
                </p>
                <p className="text-yellow-700">
                  {isBulkOperation
                    ? 'All selected files will be permanently deleted from your storage.'
                    : 'This file will be permanently deleted from your storage.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Items List */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">
              {isBulkOperation ? 'Items to be deleted:' : 'Item to be deleted:'}
            </h3>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {items.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0 text-gray-400">
                    {getIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(item.size)} • {new Date(item.modified).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Double Confirmation for Bulk Operations */}
          {needsDoubleConfirmation && (
            <div className="mt-6">
              {!confirmed ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-red-800 mb-1">
                        Bulk deletion detected
                      </p>
                      <p className="text-red-700">
                        You are about to delete {items.length} items. This is a large deletion operation.
                      </p>
                      <p className="text-red-700 mt-2">
                        Please click "Confirm" again to proceed.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex gap-3">
                    <Eye className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-700">
                      <p className="font-medium text-green-800">
                        Confirmation received
                      </p>
                      <p>
                        Click "Delete {items.length} Items" to proceed with the deletion.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isDeleting}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isDeleting}
              className={`flex-1 px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                needsDoubleConfirmation && !confirmed
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {isDeleting ? (
                <>
                  <div className="inline-flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </div>
                </>
              ) : needsDoubleConfirmation && !confirmed ? (
                'Confirm'
              ) : isBulkOperation ? (
                `Delete ${items.length} Items`
              ) : (
                'Delete Item'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}